import { Component, inject, OnDestroy } from '@angular/core';
import { DateFilterComponent } from '../../../../../_shared/components/filters/date-filter/date-filter.component';
import { VerticalBarChartComponent, VerticalBarChartData } from '../../../../../_shared/components/graphs/vertical-bar-chart/vertical-bar-chart.component';
import { GraphErrorStateComponent } from '../../../../../_shared/components/graphs/graph-error-state/graph-error-state.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { DateInterval } from '../../../../../_shared/enums';
import { DateIntervalFilterData } from '../../../../../_shared/models';
import { AccountDateFilterRequest } from '../../../../../_shared/models/account-date-filter-request.model';
import { Response } from '../../../../../_shared/models/response.model';
import { DriverUtilisationData, UtilizationTimeSeries } from '../../../models/operation-utilization.model';
import { startOfDay, endOfDay, startOfWeek, endOfWeek } from 'date-fns';
import { DriverOperationsService } from '../../../services/driver-operations.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { FilterService } from '../../../../../_shared/services';
import { Subject, Subscription } from 'rxjs';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-driver-utilisation-graph',
  standalone: true,
  imports: [
    TranslocoPipe,
    DateFilterComponent,
    VerticalBarChartComponent,
    GraphErrorStateComponent,
    EmptyStateComponent
],
  templateUrl: './driver-utilisation-graph.component.html',
  styleUrl: './driver-utilisation-graph.component.scss'
})
export class DriverUtilisationGraphComponent implements OnDestroy {
  private driverOperationsService = inject(DriverOperationsService);
  private errorHandlerService = inject(ErrorHandlerService);
  private filterService = inject(FilterService);
  private readonly transloco = inject(TranslocoService);

  private filterSubscription: Subscription;
  private translationSubscription: Subscription;

  private readonly weekdayLabelKeys: string[] = [
    'driverDashboard.graphs.weekdays.mon',
    'driverDashboard.graphs.weekdays.tue',
    'driverDashboard.graphs.weekdays.wed',
    'driverDashboard.graphs.weekdays.thu',
    'driverDashboard.graphs.weekdays.fri',
    'driverDashboard.graphs.weekdays.sat',
    'driverDashboard.graphs.weekdays.sun'
  ];

  isLoading: boolean = true;
  hasError: boolean = false;
  utilisationData: DriverUtilisationData[] = [];
  chartData: VerticalBarChartData[] = [];
  utilizationDateFilter: DateIntervalFilterData = { interval: DateInterval.Day } as DateIntervalFilterData;
  defaultInterval: DateInterval = DateInterval.Day;
  disabledIntervals: DateInterval[] = [DateInterval.Month, DateInterval.Half];
  currentDate: Date = new Date();
  accountDateFilterRequest: AccountDateFilterRequest = {
    accountIds: [],
    timeFrom: startOfDay(this.currentDate),
    timeTo: endOfDay(this.currentDate),
    interval: 0
  };

  private cancelSubject: Subject<void> = new Subject<void>();

  constructor() {
    this.filterSubscription = this.filterService.filterUpdated$.subscribe((filter) => {
      // Guard: Skip if filter is empty/null
      if (Object.values(filter).every(x => x === null) || filter === null) {
        return;
      }
      this.accountDateFilterRequest.accountIds = filter.accountIds;
      this.getDriverUtilizationTimeSeries();
    });

    // The language switch is live: re-translate the weekday labels once the new language has loaded.
    this.translationSubscription = this.transloco.selectTranslation().subscribe(() => this.buildChartData());
  }

  ngOnDestroy(): void {
    this.filterSubscription?.unsubscribe();
    this.translationSubscription?.unsubscribe();
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  async getDriverUtilizationTimeSeries(): Promise<void> {
    this.hasError = false;
    this.isLoading = true;

    try {
      const currentDate = new Date();
      let request: AccountDateFilterRequest | undefined;

      if (this.utilizationDateFilter.interval === DateInterval.Day) {
        request = {
          ...this.accountDateFilterRequest,
          interval: this.utilizationDateFilter.interval,
          timeFrom: startOfDay(currentDate),
          timeTo: endOfDay(currentDate)
        };
      } else if (this.utilizationDateFilter.interval === DateInterval.Week) {
        request = {
          ...this.accountDateFilterRequest,
          interval: this.utilizationDateFilter.interval,
          timeFrom: startOfWeek(currentDate, { weekStartsOn: 1 }),
          timeTo: endOfWeek(currentDate, { weekStartsOn: 1 })
        };
      }

      if (request) {
        this.cancelSubject.next();

        if (this.utilizationDateFilter.interval === DateInterval.Day) {
          const response = await this.driverOperationsService.getDriverUtilizationTimeSeries(request, this.cancelSubject);
          this.utilisationData = this.getDailyUtilizationGraphData(response);
        } else if (this.utilizationDateFilter.interval === DateInterval.Week) {
          const response = await this.driverOperationsService.getDriverUtilizationTimeSeries(request, this.cancelSubject);
          this.utilisationData = this.getWeeklyUtilizationGraphData(response);
        } else {
          this.utilisationData = [];
        }
      }

      this.buildChartData();

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  private getDailyUtilizationGraphData(response: Response<UtilizationTimeSeries[]>): DriverUtilisationData[] {
    const labels = ['10AM', '12PM', '2PM', '4PM', '6PM', '8PM', '10PM', '12AM', '2AM', '4AM', '6AM', '8AM'];

    const aggregatedData: Record<string, number> = {};
    labels.forEach(hour => {
      aggregatedData[hour] = 0;
    });

    response.data?.forEach(item => {
      const date = new Date(item.timeSeries);
      const hour = date.getHours();

      const index = Math.floor(hour / 2);
      if (index >= 0 && index < labels.length) {
        const interval = labels[index];
        aggregatedData[interval] += item.percentage;
      }
    });

    return labels.map(label => ({
      label,
      value: Math.round(aggregatedData[label])
    }));
  }

  private getWeeklyUtilizationGraphData(response: Response<UtilizationTimeSeries[]>): DriverUtilisationData[] {
    const labelKeys = this.weekdayLabelKeys;
    const values: number[] = new Array(7).fill(0);

    response.data?.forEach(item => {
      const date = new Date(item.timeSeries);
      const day = date.getDay() === 0 ? 6 : date.getDay() - 1;
      values[day] = Math.round(item.percentage);
    });

    return labelKeys.map((labelKey, index) => ({
      label: this.transloco.translate(labelKey),
      labelKey,
      value: values[index]
    }));
  }

  private buildChartData(): void {
    this.chartData = this.utilisationData.map(item => ({
      label: item.labelKey ? this.transloco.translate(item.labelKey) : item.label,
      value: item.value,
      color: this.getBarColor(item.value)
    })) as VerticalBarChartData[];
  }

  get hasData(): boolean {
    return this.chartData.length > 0;
  }

  formatPercent = (value: number): string => `${value.toLocaleString('default', { maximumFractionDigits: 2 })}%`;

  onDateFilterChange(filter: DateIntervalFilterData): void {
    this.utilizationDateFilter = filter;
    this.getDriverUtilizationTimeSeries();
  }

  private getBarColor(percentage: number): string {
    if (percentage >= 75) return '#28A745'; // Green
    if (percentage >= 25) return '#FFC107'; // Amber
    return '#FF7F00'; // Orange
  }
}